import {
  type GetAllIntakeFormsForOrgIdQuery,
  LanguageCode,
} from "@frontend/lib/graphql/generated-gql";
import { fromPromise } from "xstate";
import { z } from "zod";
import { translateIntakeForm } from "../../lib/intake/translateIntakeForm";
import {
  getAllIntakeFormsForOrgId,
  getIntakeFormsAndQuestionsByFormIds,
  getRetrievableIntakeResponsesByIds,
} from "../../server-actions/intake-actions";
import type { ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema
 */
const FetchAppointmentPrerequisitesInputSchema = z.object({
  userId: z.string().optional(),
  consumerId: z.string().optional(),
  orgId: z.number(),
  practiceId: z.number(),
  sex: z.string().optional(),
});

type FetchAppointmentPrerequisitesInput = z.infer<
  typeof FetchAppointmentPrerequisitesInputSchema
>;

/**
 * Success Schema
 */
export const IntakeFormSectionSchema = z.object({
  formId: z.string(),
  formName: z.string().optional(),
  sectionName: z.string(),
  sectionText: z.string(),
  priority: z.number(),
  sectionTranslations: z.array(z.any()),
  questions: z.array(z.any()),
});

const FetchAppointmentPrerequisitesSuccessSchema = z.object({
  formIds: z.array(z.number()),
  intakeFormSections: z.array(IntakeFormSectionSchema).optional(),
  responses: z.record(z.unknown()).optional(),
});

type FetchAppointmentPrerequisitesSuccess = z.infer<
  typeof FetchAppointmentPrerequisitesSuccessSchema
>;

/**
 * Error Schema
 */
const FetchAppointmentPrerequisitesErrorSchema = z.object({
  code: z.enum([
    "APPOINTMENT_NOT_FOUND",
    "USER_NOT_FOUND",
    "CONSUMER_NOT_FOUND",
    "NO_FORMS_CONFIGURED",
    "SERVICE_ERROR",
    "NETWORK_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
});

type FetchAppointmentPrerequisitesError = z.infer<
  typeof FetchAppointmentPrerequisitesErrorSchema
>;

/**
 * Dependencies
 */
export interface FetchAppointmentPrerequisitesDependencies {
  apiClient: {
    get: (url: string) => Promise<Response>;
    post: (url: string, data: unknown) => Promise<Response>;
  };
}

/**
 * Actor Implementation
 */
const setupIntakeActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: FetchAppointmentPrerequisitesInput & {
      deps: FetchAppointmentPrerequisitesDependencies;
    };
  }): Promise<FetchAppointmentPrerequisitesSuccess> => {
    const { consumerId, orgId } = input;

    try {
      const intakeFormIdsQueryResult: GetAllIntakeFormsForOrgIdQuery =
        await getAllIntakeFormsForOrgId(orgId);

      console.log("Fetched intake form IDs for orgId", orgId);
      console.log("intakeFormIdsQueryResult", intakeFormIdsQueryResult);

      const intakeFormsIds = intakeFormIdsQueryResult.intakeForms.map(
        (form) => form.id,
      );

      const intakeForms =
        await getIntakeFormsAndQuestionsByFormIds(intakeFormsIds);

      // Translate intake forms in whatever locale user is on the kiosk
      const translatedIntakeForms = intakeForms.map((form) => ({
        ...form,
        intakeFormToQuestions: translateIntakeForm(
          form.intakeFormToQuestions,
          LanguageCode.En, // @TODO: need to add other languages
        ).sort((a, b) => a.questionNumber - b.questionNumber),
      }));

      // Build section metadata and collect question IDs
      const sections = new Map<
        string,
        {
          formId: string;
          formName: string;
          sectionName: string;
          sectionText: string;
          priority: number;
          // biome-ignore lint/suspicious/noExplicitAny: FIXME:
          sectionTranslations: any[];
          questions: (typeof translatedIntakeForms)[0]["intakeFormToQuestions"];
        }
      >();
      const questionIds: number[] = [];

      // Make sections out of intake questions
      for (const form of translatedIntakeForms) {
        for (const question of form.intakeFormToQuestions) {
          const section = question.intakeQuestion.intakeFormSection;
          const sectionId = section?.id?.toString() ?? "";

          questionIds.push(question.intakeQuestion.id);

          if (sectionId && !sections.has(sectionId)) {
            sections.set(sectionId, {
              formId: form.id.toString(),
              formName: form.name,
              sectionName: section?.name ?? "",
              sectionText: section?.text ?? "",
              priority: section?.priority ?? 0,
              sectionTranslations: section?.intakeFormSectionTranslations ?? [],
              questions: form.intakeFormToQuestions.filter(
                (q) =>
                  q.intakeQuestion.intakeFormSection?.id?.toString() ===
                  sectionId,
              ),
            });
          }
        }
      }

      // Fetch responses after collecting question IDs @TODO: actually use these answers
      let retrievableIntakeResponses: Awaited<
        ReturnType<typeof getRetrievableIntakeResponsesByIds>
      > = [];

      console.log("consumerId:", consumerId);

      console.log("intakeForms", intakeForms);

      // Get saved responses only if consumerId is provided
      if (consumerId) {
        retrievableIntakeResponses = await getRetrievableIntakeResponsesByIds(
          questionIds,
          consumerId,
        );
      }

      // if one of the questions is sex (ssqid 18) then set the response to the question in the form
      // to be what we have in the context
      const sexQuestion = translatedIntakeForms
        .flatMap((form) => form.intakeFormToQuestions)
        .find(
          (question) =>
            question.intakeQuestion.superscriptIntakeQuestion?.id === 18,
        );

      const sexToChoiceIdMap = {
        male: "0",
        female: "1",
        unknown: "2",
      };

      if (sexQuestion && input.sex) {
        retrievableIntakeResponses.push({
          intakeQuestionId: sexQuestion.intakeQuestion.id,
          consumerId: consumerId ?? "",
          updatedAt: new Date().toISOString(),
          intakeQuestion: sexQuestion.intakeQuestion,
          response: sexToChoiceIdMap[input.sex.toLowerCase()],
        });
      }

      return {
        formIds: intakeFormsIds,
        intakeFormSections: sections ? Array.from(sections.values()) : [],
        responses: retrievableIntakeResponses.reduce((acc, resp) => {
          acc[resp.intakeQuestionId] = resp.response;
          return acc;
        }, {}),
      };
    } catch (error) {
      // Re-throw typed errors
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        "message" in error
      ) {
        throw error;
      }

      // Wrap unknown errors
      const wrappedError: FetchAppointmentPrerequisitesError = {
        code: "NETWORK_ERROR",
        message: error instanceof Error ? error.message : "Network error",
        retryable: true,
      };
      throw wrappedError;
    }
  },
);

/**
 * Actor with Metadata
 */
export const setupIntakeActor: ActorWithMetadata = {
  impl: setupIntakeActorImpl,
  metadata: {
    displayName: "setupIntake",
    description:
      "Fetch all data needed for intake setup (formIds, appointment, user, consumer)",
    category: "data",
    retryable: true,
    timeout: 15000, // 15 seconds
    errorCodes: [
      "APPOINTMENT_NOT_FOUND",
      "USER_NOT_FOUND",
      "CONSUMER_NOT_FOUND",
      "NO_FORMS_CONFIGURED",
      "SERVICE_ERROR",
      "NETWORK_ERROR",
    ],
    inputSchema: FetchAppointmentPrerequisitesInputSchema,
    successSchema: FetchAppointmentPrerequisitesSuccessSchema,
    errorSchema: FetchAppointmentPrerequisitesErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as FetchAppointmentPrerequisitesSuccess;
      return {
        formIds: success.formIds,
        intakeFormSections: success.intakeFormSections,
      };
    },
    onError: (error: unknown) => {
      const err = error as FetchAppointmentPrerequisitesError;
      return {
        prerequisitesError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
